// Display formatters for the legacy "MMM YY" month-tag convention used
// across the app's payment / common-charges / maintenance records.
//
// Storage format: "May 26"  (May 2026, two-digit year).
// Display format: "May 2026" — disambiguates from "26th of May" which
// is what most users read the storage form as.
//
// Keep this in `src/utils/` so the formatter is a single source of
// truth: components display via formatMonthLabel(p.mo), but the
// stored value on the record stays unchanged for back-compat.

/**
 * Convert a stored month tag to a human-readable label.
 *
 *   "May 26" → "May 2026"
 *   "Apr 25" → "Apr 2025"
 *   ""       → "—"
 *   null     → "—"
 *   "Apr 2025" (already long-form) → "Apr 2025"
 */
export function formatMonthLabel(mo) {
    if (!mo) return "—";
    const s = String(mo).trim();
    // Already in long form (4-digit year) — return as-is.
    if (/^[A-Za-z]+\s+\d{4}$/.test(s)) return s;
    // Standard "MMM YY" → "MMM 20YY". Only expand when the implied year
    // is plausibly within ±5 years of today — otherwise the value is
    // probably bad data (e.g. an old test record where `mo` got the
    // day-of-month written in instead of the year, like "Apr 01" from a
    // 2026-04-01 record). Naively expanding "Apr 01" → "Apr 2001"
    // surfaced as a confusing 25-year-old date in the UI.
    const m = s.match(/^([A-Za-z]+)\s+(\d{2})$/);
    if (m) {
        const yy = parseInt(m[2], 10);
        const yyyy = yy < 70 ? 2000 + yy : 1900 + yy;
        const currentYear = new Date().getFullYear();
        if (Math.abs(yyyy - currentYear) > 5) {
            // Implausible — keep the raw stored value rather than mislead.
            return s;
        }
        return `${m[1]} ${yyyy}`;
    }
    return s;
}

const MONTH_ABBR = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/**
 * Numeric sort key for a stored "MMM YY" month tag — higher = newer.
 *
 *   "Jun 26" → 24317   (2026*12 + 5)
 *   "May 26" → 24316
 *   "Apr 26" → 24315
 *   "Dec 25" → 24311   (correctly older than "Jan 26")
 *
 * Returns -Infinity for tags we can't parse (e.g. advance-only or
 * maintenance rows that carry an emoji-prefixed label), so they sink to
 * the bottom of a newest-first sort. Use it as:
 *
 *   list.sort((a, b) => monthSortValue(b.mo) - monthSortValue(a.mo));
 *
 * This replaces the old `new Date("Jun 26")` approach, which JS can't
 * reliably parse ("26" reads as a day, not a year) and which silently
 * fell back to an alphabetical month compare (Apr < Jun < May) — putting
 * the current month in the middle of the list instead of the top.
 */
const MONTHS_DISPLAY = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Human-readable rent cycle covered by a payment month tag.
 *
 *   rentPeriod("Jun 26", 1) → "1 May – 31 May 2026"
 *
 * Rent is paid in ARREARS (this month's payment covers the previous
 * month's stay), so the window runs from the previous month's due day
 * to the day before this month's due day. Returns "" for tags that
 * aren't "MMM YY" (advance rows, "🔧" maintenance rows). Shared by the
 * landlord PayPage card and the tenant payment cards so both sides
 * describe the same period identically.
 */
export function rentPeriod(mo, rentDueDay) {
    if (!mo || typeof mo !== "string") return "";
    const parts = mo.trim().split(/\s+/);
    if (parts.length < 2) return "";
    const mIdx = MONTHS_DISPLAY.indexOf(parts[0]);
    if (mIdx < 0) return "";
    const yy = parseInt(parts[1], 10);
    if (Number.isNaN(yy)) return "";
    const year = yy < 100 ? 2000 + yy : yy;
    const dueDay = Math.min(Math.max(parseInt(rentDueDay, 10) || 1, 1), 28);
    const start = new Date(year, mIdx - 1, dueDay);
    const end = new Date(year, mIdx, dueDay);
    end.setDate(end.getDate() - 1);
    const fmt = (d) => `${d.getDate()} ${MONTHS_DISPLAY[d.getMonth()]}`;
    return `${fmt(start)} – ${fmt(end)} ${end.getFullYear()}`;
}

export function monthSortValue(mo) {
    if (!mo) return -Infinity;
    const m = String(mo).trim().match(/^([A-Za-z]{3,})\s+(\d{2,4})$/);
    if (!m) return -Infinity;
    const mi = MONTH_ABBR.indexOf(m[1].slice(0, 3).toLowerCase());
    if (mi < 0) return -Infinity;
    let yy = parseInt(m[2], 10);
    if (yy < 100) yy = yy < 70 ? 2000 + yy : 1900 + yy;
    return yy * 12 + mi;
}
